import { IAppState, IAuditLogEdit, IDay, IEngineStatus, ILog, ILogAlertEntry, ILogAutoType, ILogCourseChangePhase, ILogLighting, ILogOnOff, ILogSpeedChange, ILogChange, ILogChangeType, IAlertLightsData, IAlertStatus, IAlertStatusType, IMonitorStatusType, IShip, IShipStatus, IShipUser, IShipVesselType, IStatus, IStatusType, ITrip, Log, Status, IGeneratorRunState } from "../../Types"
import { Ship } from "../../App/Ship"
import { logRosterChanges } from "./rosterChanges"
import { engineRunStartStatus, sailStageReplacedAt } from "./status"
import { alertProblemSummary } from "../UserAdvice/AlertMessage"
import { lightingTranslationKey } from "../../Components/LogButton/Lighting"

// One alert transition, as a log row (#916). Names the alert and what happened to
// it, attributing the crew actions to whoever performed them.
//
// Only the actor is surfaced (@mutle): the entry also carries the skipper and the
// on-watch crew at that moment, but that belongs in the record rather than on the
// row -- it is stored because it cannot be reconstructed once the watch plan moves
// on, not because it needs displaying.
//
// A missing name falls back to the unattributed wording rather than rendering an
// empty clause: an actor who has since left the ship no longer resolves, and losing
// the fact that the alarm was silenced at all would be far worse than losing who
// did it.
function alertTransitionComment(entry: ILogAlertEntry, status: IStatus | undefined, app: IAppState, t: (key: string, values?: Record<string, string>) => string, shipUsers: IShipUser[] | undefined, vesselType: IShipVesselType): string {
  const alert = t(`alert_${entry.alert}`);
  const name = entry.by != undefined ? shipUsers?.find((u) => u.id == entry.by)?.shortname : undefined;
  const transition = name
    ? t(`alert_${entry.action}_by`, { alert, name })
    : t(`alert_${entry.action}`, { alert });
  if (!status?.data) {
    return transition;
  }
  const problem = alertProblemSummary(app, status, vesselType);
  if (entry.alert != IAlertStatusType.lights) {
    return problem ? t(`alert_${entry.alert}_detail`, { transition, problem }) : transition;
  }
  try {
    const lights = Status.parse(status).dataParsed as IAlertLightsData;
    if (lights.recommended == undefined) {
      return transition;
    }
    const recorded = lights.recorded ?? ILogLighting.none;
    return t("alert_lights_detail", {
      transition,
      recorded: app.t(lightingTranslationKey(recorded, vesselType)),
      recommended: app.t(lightingTranslationKey(lights.recommended, vesselType)),
    });
  } catch {
    return transition;
  }
}

// Context the NMEA-summary helpers need. They used to read this from React
// context via hooks (`GetApp`, `GetShip`, ...), but they are plain functions and
// were being called inside `useMemo` in `LogView`, which violates the rules of
// hooks (React logged "Do not call Hooks inside useMemo" on every render and
// warned about a changing hook order). Callers now pass the values in from their
// own top-level hooks, keeping these helpers pure.
export interface LogNMEAContext {
  app: IAppState;
  ship: IShip;
  trip?: ITrip;
  shipUsers?: IShipUser[];
  logs?: ILog[];
  // Shutdown duration needs the full history, not just intervals touching this row.
  dayStatuses?: IStatus[];
  // The day a log belongs to and its edit history, needed to derive what a day-edit
  // log entry announces (issue #1285) -- the entry only names the `AuditLogEdit`
  // rows, it carries no copy of the change. Optional: a caller that doesn't supply
  // them (or a log recorded before the switch) falls back to the legacy
  // `LogMetadata.changes` snapshot.
  day?: IDay;
  auditLogEdits?: IAuditLogEdit[];
}

function pauseStopText(log: ILog, pauseStatus: IStatus | undefined, logs: ILog[], app: IAppState, t: (key: string, values?: Record<string, string>) => string): string {
  const statusName = pauseStatus?.value != undefined ? IShipStatus[pauseStatus.value] : undefined;
  const statusText = statusName != undefined && pauseStatus?.value != IShipStatus.none && pauseStatus?.value != IShipStatus.inmotion
    ? t(`status_${statusName}`)
    : undefined;
  const startLog = pauseStatus && logs.find((l) => l.id == pauseStatus.startLogId);
  const duration = startLog ? app.format("time", log.timeUtc - startLog.timeUtc) : undefined;
  if (statusText) {
    return duration
      ? t("pause_stop_status_duration", { status: statusText, duration })
      : t("pause_stop_status", { status: statusText });
  }
  if (duration) {
    return t("pause_stop_duration", { duration });
  }
  return t("pause_stop");
}

export function LogCommentText(log: ILog, statuses: IStatus[], ctx: LogNMEAContext): string {
  return [LogNMEAInfo(log, statuses, ctx), LogNMEAComment(log, statuses, ctx), log.comment]
    .filter((comment) => comment)
    .join(" - ");
}

export function LogNMEAInfo(log: ILog, statuses: IStatus[], ctx: LogNMEAContext): string | undefined {
  const { app, ship, trip, logs = [] } = ctx;
  const t = app.getT("LogView_log");
  if (trip?.importedAt && log.comment) {
    return undefined;
  }

  const metadata = Log.parse(log).dataParsed;
  const shipStatus = (() => {
    const shipStatuses = statuses.filter((s) => s.statusType == IStatusType.ship);
    const status = shipStatuses.find((s) => log.id > 0 && s.startLogId == log.id && s.endLogId != log.id) || shipStatuses[shipStatuses.length - 1];
    return status;
  })();
  const shipStatusData = (() => {
    if (shipStatus && shipStatus.data) {
      const parsed = Status.parse(shipStatus);
      if (parsed.statusType === IStatusType.ship) {
        return parsed.dataParsed;
      }
    }
    return undefined;
  })();
  const endStatuses = statuses.filter((s) => s.endLogId == log.id);
  const startStatuses = statuses.filter((s) => s.startLogId == log.id);
  const _engineStart = startStatuses.find((s) => s.statusType == IStatusType.engine && s.startLogId == log.id);
  // Matched on the engine's own `item` where the entry names one, so a twin whose other
  // engine closes an interval on the same log can't be picked up here (#1819 testing).
  const engineStop = endStatuses.find((s) => s.statusType == IStatusType.engine
    && (metadata.engineNumber == undefined || s.item == metadata.engineNumber));
  const engineSuffix = ship.engineCount > 1 ? "_name" : "";

  switch (metadata.type) {
    case ILogAutoType.engine:
      if (metadata.engineStatus == ILogOnOff.on) {
        // #1819: an engine that comes up below the idle RPM threshold records the run
        // state it started in, so the entry says so instead of a bare "Started engine".
        // Entries recorded before that carry no run state and keep the plain wording.
        const idleStart = metadata.engineRunState == IEngineStatus.idle ? "_idle" : "";
        return t(`engine_start${idleStart}${engineSuffix}`, { name: Ship.engineName(ship, metadata.engineName, metadata.engineNumber) });
      } else {
        // The engine's run spans every interval that meets end-to-end back to the start
        // (#1819 testing): idle crossings split one run into several, and the interval
        // closing here is only its last segment.
        const runStart = engineRunStartStatus(ctx.dayStatuses ?? statuses, engineStop);
        const startLog = logs.find((l) => runStart && runStart.startLogId > 0 && l.id == runStart.startLogId);
        const engineMessage = metadata.engineStatus == ILogOnOff.missing ? "engine_missing" : "engine_stop";
        const distance = (startLog && log.distance != undefined && startLog.distance != undefined) ? app.format("distance", log.distance - startLog.distance) : "";
        const duration = (startLog && log.timeUtc != undefined && startLog.timeUtc != undefined) ? app.format("time", log.timeUtc - startLog.timeUtc) : "";
        // Each key only names what it is given: no start log means no duration and no
        // distance, and a run with no measurable distance still reports its duration.
        // Getting that mapping wrong is what printed a literal "{duration}".
        const detail = !duration ? "" : distance ? "_distance" : "_duration";
        return t(`${engineMessage}${detail}${engineSuffix}`, {
          name: Ship.engineName(ship, metadata.engineName, metadata.engineNumber),
          duration: duration,
          distance: distance,
        });
      }
    case ILogAutoType.engineRunState: {
      // #1819: a still-running engine crossed the idle RPM threshold. Going into idle
      // is a plain state change; coming back under load names how long it idled,
      // measured over the idle interval that ends at this log (matched on the engine's
      // own `item`, so a second engine's status can't be picked up here).
      const name = Ship.engineName(ship, metadata.engineName, metadata.engineNumber);
      if (metadata.engineRunState == IEngineStatus.idle) {
        return t(`engine_idle${engineSuffix}`, { name });
      }
      const idleStatus = endStatuses.find((s) => s.statusType == IStatusType.engine && s.item == metadata.engineNumber);
      const startLog = logs.find((l) => idleStatus && l.id == idleStatus.startLogId);
      const duration = (startLog && log.timeUtc != undefined && startLog.timeUtc != undefined)
        ? app.format("time", log.timeUtc - startLog.timeUtc)
        : "";
      if (!duration) {
        return t(`engine_load${engineSuffix}`, { name });
      }
      return t(`engine_load_duration${engineSuffix}`, { name, duration });
    }
    case ILogAutoType.interval:
      return t("auto_interval", { duration: app.format("time", metadata.intervalDuration) });
    case ILogAutoType.shipStatus:
      if (metadata.shipStatus != undefined) {
        const location = shipStatusData && shipStatusData.location;
        if (location) {
          return t("status_location", { status: t(`status_${IShipStatus[metadata.shipStatus]}`), location: location });
        } else {
          return t(`status_${IShipStatus[metadata.shipStatus]}`);
        }
      } else {
        return t(`status_${IShipStatus[IShipStatus.none]}`);
      }
      break;
    case ILogAutoType.navigationStatus:
      return t(`navigation_status_${metadata.navigationStatus == ILogOnOff.on ? "start" : "stop"}`);
      break;
    case ILogAutoType.nmeaConnection:
      if (metadata.nmeaConnectionUserInitiated) {
        return t(`nmea_connection_${metadata.nmeaConnection == ILogOnOff.on ? "start" : "user_stop"}`);
      } else {
        return t(`nmea_connection_${metadata.nmeaConnection == ILogOnOff.on ? "resume" : "stop"}`);
      }
      break;
    case ILogAutoType.stop: {
      const stopText = metadata.stopLocation
        ? t("stop_location", { stop: t(`stop_type_${metadata.stopType}`), location: metadata.stopLocation })
        : t("stop", { stop: t(`stop_type_${metadata.stopType}`) });
      // #1219 follow-up: the stop dialog makes the crew pick a ship status (bridge,
      // lock, bunkering, ...), but the stop metadata never carries it -- the choice is
      // written onto the ship status record instead. Resolve it the same way a
      // `shipStatus` entry resolves its own and append it as a second segment, using
      // the separator `LogView` already puts between the parts of a log row.
      //
      // Deriving it here rather than freezing it into the metadata means stops already
      // in the logbook gain the text too, and it follows a status corrected afterwards
      // from the log row's dropdown. `none`/`inmotion` are skipped: a stop resolving to
      // "In motion" is a pre-#1219 entry that never recorded a status, and naming it
      // would read worse than the plain phrase.
      const statusValue = shipStatus?.value;
      const statusText = (statusValue != undefined && statusValue != IShipStatus.none && statusValue != IShipStatus.inmotion)
        ? t(`status_${IShipStatus[statusValue]}`)
        : undefined;
      return [stopText, statusText].filter((s) => s).join(" - ");
    }
    case ILogAutoType.courseChange: {
      const phase = metadata.nmeaCourseChangePhase;
      if (phase == ILogCourseChangePhase.begin) {
        return t("course_change_started");
      }
      const prev = metadata.nmeaCourseChangePrevious;
      const next = metadata.nmeaCourseChangeNew;
      const duration = metadata.nmeaCourseChangeDuration;
      // Legacy logs missing the turn components fall back to the generic phrase.
      if (prev == undefined || next == undefined || duration == undefined) {
        return t("course_change");
      }
      // Signed shortest turn in (-180, 180]: positive turns to starboard, negative to port.
      const signed = ((next - prev + 540) % 360) - 180;
      const angle = app.format("course", Math.abs(signed));
      const side = t(signed >= 0 ? "course_change_side_starboard" : "course_change_side_port");
      const time = app.format("time", duration);
      if (phase == ILogCourseChangePhase.active) {
        // Live, still turning: point at the heading the ship is steering towards now.
        return t("course_change_active", { angle, side, duration: time, course: app.format("course", next) });
      }
      // Settled end summary (phase .end) or a legacy committed log (no phase): reference
      // the course the ship left.
      return t("course_change_end", { angle, side, duration: time, course: app.format("course", prev) });
    }
    case ILogAutoType.speedChange: {
      if (metadata.speedChange == ILogSpeedChange.stop) {
        return t("pause_start");
      }
      const pauseStatus = endStatuses.find((s) => s.statusType == IStatusType.ship);
      return pauseStopText(log, pauseStatus, logs, app, t);
    }
    case ILogAutoType.propulsion:
      // #1227: the crew's answer to the "engine entry missing?" hint. Only the "no
      // engine running" answer is recorded today — a confirmed engine start is written
      // as a real `.engine` entry instead.
      //
      // The hint is offered on every vessel type, so the wording has to suit a motor
      // vessel too: "under sail" is nonsense on a ship with no sails, and the marker
      // only ever records that no engine was running. Gated on `vesselType` and
      // deliberately NOT on the hint's `reason` — a motor vessel leaving a berth
      // reports `castOff`, not `motorVessel` (see `castOffWinsOverMotorVessel`), so a
      // reason-gate would pick the sail wording for most motor-vessel departures.
      if (metadata.propulsion == ILogOnOff.off) {
        return t(ship.vesselType == IShipVesselType.motor ? "propulsion_off_noSails" : "propulsion_off");
      }
      break;
    case ILogAutoType.engineMissing:
      // #1227: the primary app instance's provisional "you're underway with no engine
      // recorded" ghost row. It is never a committed entry — the crew's answer replaces
      // it with a real engine or propulsion record.
      //
      if (metadata.engineMissing) {
        return t(`engine_missing_hint_${metadata.engineMissing.reason}`);
      }
      break;
    case ILogAutoType.dataRecord: {
      // #615's N2K-driven generator on/off, folded into the generic `dataRecord`
      // entry (see `LogMetadata.dataRecord(...)`) rather than the legacy
      // `IStatusType.generator` Status lookup (still handled separately below, in
      // `LogNMEAComment`, for the old importer-only path). Reuses the exact same
      // `generator_start`/`generator_stop` translation keys so the rendered text is
      // unchanged for users regardless of which path produced the log. Only the
      // first generator entry is rendered -- today's N2K wiring only ever
      // transitions one instance per log.
      const generatorEntry = metadata.dataRecord?.generator?.[0];
      if (generatorEntry) {
        const running = generatorEntry.status == IGeneratorRunState.starting || generatorEntry.status == IGeneratorRunState.running;
        return t(running ? "generator_start" : "generator_stop", { number: generatorEntry.number });
      }
      break;
    }
    case ILogAutoType.lighting: {
      // #1216: navigation-lights entries used to render with no text at all -- this
      // case was missing, and `LogNMEAComment` bails out on the `nmeaAuto: false`
      // metadata `LogMetadata.lighting(...)` writes, so nothing described them.
      // `StatelessNavigation.record(log:lighting:)` writes both the metadata and a
      // `.lighting` status opening on this log, so fall back to the status when a log
      // carries no value of its own. The recorded signal is named with the same
      // `LogButton_lighting_*` key the lights alert (`UserAdvice/AlertView.tsx`) and
      // the LogButton menu use, keeping all three consistent.
      const lightingStatus = startStatuses.find((s) => s.statusType == IStatusType.lighting);
      // `none` is 0, so this has to test against undefined rather than falsiness.
      const lighting = metadata.lighting ?? (lightingStatus?.value as ILogLighting | undefined);
      if (lighting == undefined) {
        break;
      }
      if (lighting == ILogLighting.none) {
        return t("lighting_none");
      }
      return t("lighting", { lighting: app.t(lightingTranslationKey(lighting, ship.vesselType)) });
    }
    default:
      break;
  }

  return undefined;
}

export function LogNMEAComment(log: ILog, statuses: IStatus[], ctx: LogNMEAContext): string | undefined {
  const { app, ship, trip, shipUsers, logs = [] } = ctx;
  const t = app.getT("LogView_log");
  if (trip?.importedAt && log.comment) {
    return "";
  }

  const metadata = Log.parse(log).dataParsed;
  const endStatuses = statuses.filter((s) => s.endLogId == log.id);
  const startStatuses = statuses.filter((s) => s.startLogId == log.id);
  const generatorStart = startStatuses.find((s) => s.statusType == IStatusType.generator && s.startLogId == log.id);
  const generatorStop = endStatuses.find((s) => s.statusType == IStatusType.generator && s.endLogId == log.id);
  const monitorStart = startStatuses.filter((s) => s.statusType == IStatusType.monitor && s.startLogId == log.id);
  const monitorStop = endStatuses.filter((s) => s.statusType == IStatusType.monitor && s.endLogId == log.id);
  const alertStart = startStatuses.filter((s) => s.statusType == IStatusType.alert && s.startLogId == log.id);
  // Every alert transition now writes its own log carrying this metadata (#916), so
  // it describes itself -- including WHICH transition it was, which the status rows
  // could not express: a cleared alert and one that resolved on its own both just
  // close the row, and an alert that was snoozed opens a fresh one that read as a
  // re-trigger. Checked before the status-derived branches below so the entry does
  // not also get described by the row it wrote.
  if (metadata.type == ILogAutoType.alert && metadata.alert) {
    const status = alertStart.find((s) => s.name == metadata.alert?.alert);
    return alertTransitionComment(metadata.alert, status, app, t, shipUsers, ship.vesselType);
  }
  if (monitorStart.length > 0) {
    return monitorStart.map((m) => m.item && t("monitor_start", { monitor: t(`monitor_${IMonitorStatusType[m.item]}`) })).join(", ");
  }
  if (monitorStop.length > 0) {
    return monitorStop.map((m) => m.item && t("monitor_stop", { monitor: t(`monitor_${IMonitorStatusType[m.item]}`) })).join(", ");
  }
  // Alerts written before #916 carry no metadata of their own, so they are still
  // described from the status row that opened on this log.
  if (alertStart.length > 0) {
    return alertStart.map((s) => t("alert_start", { alert: t(`alert_${s.name}`), status: t(`alert_status_${IAlertStatus[s.value]}`) })).join(", ");
  }
  const sailsUp = startStatuses.filter((s) => s.statusType == IStatusType.sails && s.startLogId == log.id);
  // A stage replaced by another stage of the same sail is not a strike -- the sail stayed
  // up and only its reef level changed, so the entry reads "Genua - Reff 2 gesetzt" alone
  // rather than pairing it with "Genua geborgen" (#1288).
  const sailsDown = endStatuses.filter((s) => s.statusType == IStatusType.sails && s.endLogId == log.id && !sailStageReplacedAt(s, statuses, log.id));
  if (sailsUp.length > 0 || sailsDown.length > 0) {
    const t_sail = app.getT("ShipDataView_sail");
    const shipSails = Ship.sails(ship);
    const getSailLabel = (name: string | undefined, value: number): string => {
      if (!name) return "";
      const sail = shipSails.find((s) => s.id === name);
      if (sail && sail.sail) {
        const sailDisplayName = t_sail(sail.sail) || sail.sail;
        return value > 0 ? t_sail("reef", { sail: sailDisplayName, reef: t_sail(`reef_${value}`) }) : sailDisplayName;
      }
      return name;
    };
    const parts = [
      ...sailsUp.map((s) => t("sail_up", { sail: getSailLabel(s.name, s.value) })),
      ...sailsDown.map((s) => t("sail_down", { sail: getSailLabel(s.name, s.value) })),
    ].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(", ");
    }
  }
  if (!metadata.nmeaAuto) {
    // Derived from the day's audit trail when the entry names the rows it announces;
    // the stored snapshot is only read for entries recorded before issue #1285 (and
    // goes away with them once the backfill data transition has run).
    const rosterChanges = logRosterChanges(log, ctx.day, ctx.auditLogEdits);
    const logChanges = rosterChanges.length > 0 ? rosterChanges : metadata.changes;
    if (logChanges && logChanges.length > 0) {
      const changes = logChanges.map((c: ILogChange) => {
        const text = ["change", c.action, c.type].join("_");
        switch (c.type) {
          case ILogChangeType.crew:
          case ILogChangeType.passengers:
          case ILogChangeType.skipper: {
            const user = shipUsers?.find((u) => u.id == c.id);
            const name = user?.name || user?.shortname;
            if (name) {
              return t(text, { name: name });
            }
            break;
          }
          default:
            break;
        }
        return null;
      }).filter(Boolean);
      if (changes.length > 0) {
        return changes.join(", ");
      }
    }
    return "";
  }
  if (generatorStart) {
    return t(`generator_start`, { number: generatorStart.item });
  }
  if (generatorStop) {
    return t(`generator_stop`, { number: generatorStop.item });
  }
  if (metadata.nmeaSpeedStartStop != undefined) {
    if (!metadata.nmeaSpeedStartStop) {
      return t("pause_start");
    }
    const pauseStatus = endStatuses.find((s) => s.statusType == IStatusType.ship);
    return pauseStopText(log, pauseStatus, logs, app, t);
  }
  if (metadata.nmeaConnectionStartStop != undefined) {
    return t(`nmea_connection_${metadata.nmeaConnectionStartStop ? "resume" : "stop"}`);
  }
  return undefined;
}
